import {
  AggHomogenizationData,
  FactorsScoresData,
  ItemData,
} from '@/api/workOrders/types';
import {
  Box,
  Divider,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { GroupStatusIcon } from '../../components/StatusIndicators';
import {
  BoldCell,
  CellContent,
  CellHeader,
  RowHeader,
  StyledTable,
} from '../../styles';
import {
  isScoresDegreesComplete,
  isScoresFundamentationComplete,
  isScoresPrecisionComplete,
} from '../utils/completionCheckers';

interface ScoresProps {
  scores: FactorsScoresData;
  aggregatedHomogenization: AggHomogenizationData;
}

interface ScoresTable {
  item: number;
  description: string;
  third: string;
  second: string;
  first: string;
  point: number;
}

interface DegreeHeader {
  label: string;
  value: string;
}

const presentScoresData = (data: ItemData[]): ScoresTable[] => {
  return [
    {
      item: 1,
      description: 'Caracterização do imóvel avaliando',
      third: 'Completa quanto a todos os fatores analisados',
      second: 'Completa quanto aos fatores utilizados no tratamento',
      first: 'Adoção de situação paradigma',
      point: data[0]?.points || 0,
    },
    {
      item: 2,
      description:
        'Quantidade mínima de dados de mercado, efetivamente utilizados',
      third: '12',
      second: '5',
      first: '3',
      point: data[1]?.points || 0,
    },
    {
      item: 3,
      description: 'Identificação dos dados de mercado',
      third:
        'Apresentação de informações relativas a todas as características ' +
        'dos dados analisados, com foto e características observadas pelo autor do laudo',
      second:
        'Apresentação de informações relativas a todas as características dos dados analisados',
      first:
        'Apresentação de informações relativas a todas as características ' +
        'dos dados correspondentes aos fatores utilizados',
      point: data[2]?.points || 0,
    },
    {
      item: 4,
      description: 'Intervalo admissível de ajuste para o conjunto de fatores',
      third: '0,80 a 1,25',
      second: '0,50 a 2,00',
      first: '0,40 a 2,50(*)',
      point: data[3]?.points || 0,
    },
  ];
};

const presentDegreesHeader = (): DegreeHeader[] => {
  return [
    { label: '1', value: 'GRAUS' },
    { label: '2', value: 'III' },
    { label: '3', value: 'II' },
    { label: '4', value: 'I' },
    { label: '5', value: 'TOTAL' },
    { label: '6', value: 'GRAU DE FUNDAMENTAÇÃO' },
  ];
};

export function Scores({
  scores,
  aggregatedHomogenization,
}: ScoresProps): JSX.Element {
  const scoresTableData = presentScoresData(scores.items);
  const degreesHeaders = presentDegreesHeader();

  return (
    <Box id="fac-scores">
      <Box display="flex" alignItems="center" gap={1}>
        <GroupStatusIcon isComplete={isScoresFundamentationComplete(scores)} />
        <Typography my={3} fontWeight={600}>
          Fundamentação
        </Typography>
      </Box>
      <TableContainer sx={{ mb: 3 }}>
        <StyledTable aria-label="tabela-pontuacao">
          <TableHead>
            <RowHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '7%' }}>
                ITEM
              </CellHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '20%' }}>
                DESCRIÇÃO
              </CellHeader>
              <CellHeader align="center" colSpan={3} style={{ width: '63%' }}>
                GRAU DE FUNDAMENTAÇÃO
              </CellHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '10%' }}>
                PONTOS OBTIDOS
              </CellHeader>
            </RowHeader>
            <RowHeader>
              <CellHeader align="center" style={{ width: '21%' }}>
                III
              </CellHeader>
              <CellHeader align="center" style={{ width: '21%' }}>
                II
              </CellHeader>
              <CellHeader align="center" style={{ width: '21%' }}>
                I
              </CellHeader>
            </RowHeader>
          </TableHead>
          <TableBody>
            {scoresTableData.map((row) => (
              <TableRow key={row.item}>
                <BoldCell align="center">{row.item}</BoldCell>
                <CellContent align="center">{row.description}</CellContent>
                <CellContent align="center" style={{ fontSize: '14px' }}>
                  {row.third}
                </CellContent>
                <CellContent align="center" style={{ fontSize: '14px' }}>
                  {row.second}
                </CellContent>
                <CellContent align="center" style={{ fontSize: '14px' }}>
                  {row.first}
                </CellContent>
                <BoldCell align="center">
                  <Typography fontWeight={600}>{row.point || '-'}</Typography>
                </BoldCell>
              </TableRow>
            ))}
            <TableRow>
              <CellContent colSpan={5} align="left">
                <Typography variant="body2" fontWeight={600}>
                  Soma
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography fontWeight={600}>{scores.total ?? '-'}</Typography>
              </CellContent>
            </TableRow>
            <TableRow>
              <CellContent colSpan={6} align="center">
                <Typography variant="body2">
                  (*) No caso de utilização de menos de 5 dados de mercado, o
                  intervalo admissível de ajuste é de 0,80 a 1,25, pois é
                  desejável que, com um número menor de dados de mercado, a
                  amostra seja menos heterogênea.
                </Typography>
              </CellContent>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
      <Divider />
      <Box display="flex" alignItems="center" gap={1}>
        <GroupStatusIcon isComplete={isScoresDegreesComplete(scores)} />
        <Typography my={3} fontWeight={600}>
          Enquadramento Segundo Grau de Fundamentação
        </Typography>
      </Box>
      <TableContainer sx={{ mb: 3 }}>
        <StyledTable aria-label="tabela-graus-fundamentacao">
          <TableHead>
            <RowHeader>
              {degreesHeaders.map((header) => (
                <CellHeader align="center" key={header.label}>
                  {header.value}
                </CellHeader>
              ))}
            </RowHeader>
          </TableHead>
          <TableBody>
            <TableRow>
              <CellContent align="center">
                <Typography fontWeight={600}>Pontos Mínimos</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography>10</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography>6</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography>4</Typography>
              </CellContent>
              <BoldCell align="center" rowSpan={2}>
                <Typography fontWeight={600}>{scores.total ?? '-'}</Typography>
              </BoldCell>
              <BoldCell align="center" rowSpan={2}>
                <Typography fontWeight={600}>
                  {scores.reasoning_level || '-'}
                </Typography>
              </BoldCell>
            </TableRow>
            <TableRow>
              <CellContent align="center">
                <Typography fontWeight={600} sx={{ mt: 1 }}>
                  Itens Obrigatórios
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ mt: 1, fontSize: '14px' }}>
                  itens 2 e 4 no Grau III, com os demais no mínimo no Grau II
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ mt: 1, fontSize: '14px' }}>
                  itens 2 e 4 no mínimo no Grau II e os demais no mínimo no Grau
                  I
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ mt: 1, fontSize: '14px' }}>
                  Todos no mínimo no Grau I
                </Typography>
              </CellContent>
            </TableRow>
            <TableRow>
              <CellContent colSpan={6} align="center">
                <Typography variant="body2">
                  Conforme ABNT NBR 14653-2 2011, item 9.2.1.1: Para atingir o
                  Grau III, é obrigatório: a) apresentação do relatório na
                  modalidade completa.
                </Typography>
              </CellContent>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
      <Divider />
      <Box display="flex" alignItems="center" gap={1}>
        <GroupStatusIcon
          isComplete={isScoresPrecisionComplete(aggregatedHomogenization)}
        />
        <Typography my={3} fontWeight={600}>
          Grau de precisão
        </Typography>
      </Box>
      <TableContainer>
        <StyledTable aria-label="tabela-grau-precisao">
          <TableHead>
            <RowHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '25%' }}>
                DESCRIÇÃO
              </CellHeader>
              <CellHeader align="center" colSpan={3} style={{ width: '50%' }}>
                GRAU DE PRECISÃO
              </CellHeader>
              <CellHeader rowSpan={2} align="center" style={{ width: '25%' }}>
                PONTOS OBTIDOS
              </CellHeader>
            </RowHeader>
            <RowHeader>
              <CellHeader align="center">III</CellHeader>
              <CellHeader align="center">II</CellHeader>
              <CellHeader align="center">I</CellHeader>
            </RowHeader>
          </TableHead>
          <TableBody>
            <TableRow>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>
                  Amplitude do intervalo de confiança de 80% em torno da
                  estimativa de tendência central
                </Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>≤ 30%</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>≤ 40%</Typography>
              </CellContent>
              <CellContent align="center">
                <Typography sx={{ fontSize: '14px' }}>≤ 50%</Typography>
              </CellContent>
              <BoldCell align="center">
                <Typography fontWeight={600}>
                  {aggregatedHomogenization.precision_level || '-'}
                </Typography>
              </BoldCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      </TableContainer>
    </Box>
  );
}
